import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  Loader2,
  Trash2,
  Pencil,
  MoreHorizontal,
  CreditCard,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SubscriptionFormDialog } from "@/components/SubscriptionFormDialog";
import { formatDateHu } from "@/components/DeductionFormDialog";

const FREQ = { monthly: "Havi", quarterly: "Negyedéves", half_year: "Féléves", yearly: "Éves" };
const CONTRACT = { fixed_term: "Határozott", open_ended: "Határozatlan" };

const Th = ({ children, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] ${className}`}>{children}</th>
);
const Td = ({ children, className = "", ...rest }) => (
  <td className={`whitespace-nowrap px-4 py-3 ${className}`} {...rest}>{children}</td>
);

const huFmt = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 2 });
function formatMoney(price, currency) {
  if (price == null) return "—";
  const c = currency || "HUF";
  return `${huFmt.format(price)} ${c}`;
}

export default function SubscriptionsPage() {
  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: subs }, { data: ps }] = await Promise.all([
        api.get("/subscriptions"),
        api.get("/partners"),
      ]);
      setItems(subs);
      setPartners(ps);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.partner_name || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const onDelete = async (s) => {
    if (!window.confirm(`Törlöd a(z) "${s.name}" előfizetést?`)) return;
    try {
      await api.delete(`/subscriptions/${s._id}`);
      setItems((prev) => prev.filter((x) => x._id !== s._id));
      toast.success("Előfizetés törölve.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header>
        <div className="flex items-center gap-1.5 text-muted-foreground/80">
          <CreditCard className="h-3.5 w-3.5" strokeWidth={2} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">Pénzügy</span>
        </div>
        <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">Előfizetések</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Partnerekhez rendelt rendszeres díjak (szoftverek, bérletek, szerződések).
        </p>
      </header>

      <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Előfizetések</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {items.length === 0 ? "Még nincs előfizetés rögzítve." : `${items.length} előfizetés`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Keresés…" value={search} onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-56 pl-8" data-testid="subscription-search" />
            </div>
            <Button size="sm" onClick={() => setOpenCreate(true)} data-testid="new-subscription-button">
              <Plus className="h-4 w-4" /> Új előfizetés
            </Button>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
              <CreditCard className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-medium">{search ? "Nincs találat" : "Nincs rögzített előfizetés"}</p>
            {!search && (
              <Button variant="outline" size="sm" className="mt-2 h-8" onClick={() => setOpenCreate(true)}>
                <Plus className="h-3.5 w-3.5" /> Új előfizetés
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                  <Th>Név</Th>
                  <Th>Partner</Th>
                  <Th className="text-right">Ár</Th>
                  <Th>Gyakoriság</Th>
                  <Th>Szerződés</Th>
                  <Th>Lejárat</Th>
                  <Th className="pr-4 text-right">Műveletek</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s._id} data-testid={`subscription-row-${s._id}`}
                    className="border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]">
                    <Td>
                      <span className="font-medium">{s.name}</span>
                      {s.description && <p className="mt-0.5 truncate text-[12px] text-muted-foreground">{s.description}</p>}
                    </Td>
                    <Td className="text-muted-foreground">{s.partner_name || "—"}</Td>
                    <Td className="text-right tabular-nums font-medium">{formatMoney(s.price, s.currency)}</Td>
                    <Td className="text-muted-foreground">{FREQ[s.billing_frequency] || s.billing_frequency}</Td>
                    <Td>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                        s.contract_type === "fixed_term"
                          ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                          : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      }`}>
                        {CONTRACT[s.contract_type] || s.contract_type}
                      </span>
                    </Td>
                    <Td className="text-muted-foreground tabular-nums">
                      {s.expiry_date ? (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {formatDateHu(s.expiry_date)}
                        </span>
                      ) : "—"}
                    </Td>
                    <Td className="pr-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button aria-label="Műveletek"
                            data-testid={`subscription-actions-${s._id}`}
                            className="ml-auto grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground">
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setEditing(s)}>
                            <Pencil className="h-4 w-4" /> Szerkesztés
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(s)}>
                            <Trash2 className="h-4 w-4" /> Törlés
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <SubscriptionFormDialog
        open={openCreate || !!editing}
        onOpenChange={(v) => { if (!v) { setOpenCreate(false); setEditing(null); } }}
        subscription={editing}
        partners={partners}
        onSaved={(saved, mode) => {
          if (mode === "create") setItems((prev) => [saved, ...prev]);
          else setItems((prev) => prev.map((s) => (s._id === saved._id ? { ...s, ...saved, partner_name: prev.find((p) => p._id === saved._id)?.partner_name } : s)));
          load();
        }}
      />
    </div>
  );
}
